import {
  Box,
  ClickAwayListener,
  IconButton,
  InputAdornment,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  SvgIcon,
  TextField,
  useTheme,
} from '@mui/material';
import { useRef } from 'react';
import type { KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { AssetType } from '@peritasai/types';
import { navigationTokens } from '@peritasai/ui-shared';
import headerSearchIcon from '../../../../assets/svg/header-search-icon.svg';
import { ASSET_TYPE_OPTIONS } from './constants';
import { popperPaperSx, popperTitleSx, searchFieldSx } from './styles';

export const PeriverseHeaderSearch = ({
  onAssetTypeSelect,
  onClearSearch,
  onSearchChange,
  onSearchFocus,
  onSearchKeyDown,
  searchValue,
  setIsDropdownOpen,
  shouldShowDropdown,
}: {
  onAssetTypeSelect: (
    type: (typeof ASSET_TYPE_OPTIONS)[number]['value']
  ) => void;
  onClearSearch: () => void;
  onSearchChange: (value: string) => void;
  onSearchFocus: () => void;
  onSearchKeyDown: (event: KeyboardEvent<HTMLInputElement>) => void;
  searchValue: string;
  setIsDropdownOpen: (isOpen: boolean) => void;
  shouldShowDropdown: boolean;
}) => {
  const { t } = useTranslation('periverse');
  const theme = useTheme();
  const searchAnchorRef = useRef<HTMLDivElement | null>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);

  const assetTypeOptions: Array<{ value: AssetType; label: string }> = [
    { value: AssetType.CASE_CART, label: t('headerSearch.assetType.caseCart') },
    { value: AssetType.TRAY, label: t('headerSearch.assetType.tray') },
    {
      value: AssetType.EQUIPMENT,
      label: t('headerSearch.assetType.equipment'),
    },
    {
      value: AssetType.STRETCHER,
      label: t('headerSearch.assetType.stretcher'),
    },
    { value: AssetType.IMPLANT, label: t('headerSearch.assetType.implant') },
  ];

  const handleClickAway = (event: MouseEvent | TouchEvent) => {
    if (searchAnchorRef.current?.contains(event.target as Node)) {
      return;
    }
    setIsDropdownOpen(false);
  };

  return (
    <Box sx={{ width: '39.23vw' }} ref={searchAnchorRef}>
      <TextField
        variant="outlined"
        size="small"
        value={searchValue}
        onChange={(event) => onSearchChange(event.target.value)}
        onKeyDown={onSearchKeyDown}
        onFocus={onSearchFocus}
        inputRef={searchInputRef}
        inputProps={{
          'aria-label': t('headerSearch.searchPlaceholder'),
          onKeyDown: (event: KeyboardEvent<HTMLInputElement>) => {
            if (event.key === 'Enter') {
              searchInputRef.current?.blur();
            }
          },
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <img src={headerSearchIcon} alt="" width={24} height={24} />
            </InputAdornment>
          ),
          endAdornment: searchValue.trim().length > 0 && (
            <InputAdornment position="end" sx={{ mr: 0.5 }}>
              <IconButton
                size="small"
                onMouseDown={(event) => event.preventDefault()}
                onClick={onClearSearch}
                aria-label="Clear search"
                sx={{ color: navigationTokens.search.icon }}
              >
                <SvgIcon viewBox="0 0 24 24" sx={{ fontSize: '1.25rem' }}>
                  <path d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z" />
                </SvgIcon>
              </IconButton>
            </InputAdornment>
          ),
        }}
        sx={searchFieldSx}
      />
      <Popper
        open={shouldShowDropdown}
        anchorEl={searchAnchorRef.current}
        placement="bottom-start"
        sx={{ zIndex: theme.zIndex.modal }}
      >
        <ClickAwayListener onClickAway={handleClickAway}>
          <Paper elevation={3} sx={popperPaperSx}>
            <Box sx={{ px: 2, py: '6px' }}>
              <Box component="p" sx={popperTitleSx}>
                {t('headerSearch.assetTypesTitle')}
              </Box>
            </Box>
            <MenuList disablePadding sx={{ pt: '0px', mt: '0px' }}>
              {assetTypeOptions.map((option) => (
                <MenuItem
                  key={option.value}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => onAssetTypeSelect(option.value)}
                  sx={{ py: '6px', px: 2 }}
                >
                  {option.label}
                </MenuItem>
              ))}
            </MenuList>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </Box>
  );
};
